import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import axios from "axios";
import { 
  Home, RotateCcw, Trophy, XCircle, CheckCircle, 
  ChevronDown, ChevronUp, Award
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ResultsPage() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  
  const [results, setResults] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [expandedQuestion, setExpandedQuestion] = useState(null);

  useEffect(() => {
    fetchResults();
  }, [sessionId]);

  const fetchResults = async () => {
    try {
      const response = await axios.get(`${API}/game/${sessionId}/review`);
      setResults(response.data);
    } catch (error) {
      console.error("Error fetching results:", error);
      toast.error("حدث خطأ في تحميل النتائج");
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusConfig = () => {
    if (!results) return {};
    
    switch (results.status) {
      case 'won':
        return {
          icon: Trophy,
          title: "مبروك! 🎉",
          subtitle: "لقد ربحت المليون!",
          color: "text-neon-gold",
          bgColor: "from-yellow-900/50 to-orange-900/50",
          iconColor: "text-neon-gold"
        };
      case 'quit':
        return {
          icon: Award,
          title: "انسحاب ذكي!",
          subtitle: "قرار حكيم بالانسحاب",
          color: "text-neon-cyan",
          bgColor: "from-cyan-900/50 to-blue-900/50",
          iconColor: "text-neon-cyan"
        };
      case 'lost':
        return {
          icon: XCircle,
          title: "للأسف!",
          subtitle: "إجابة خاطئة",
          color: "text-red-400",
          bgColor: "from-red-900/50 to-red-950/50",
          iconColor: "text-red-400"
        };
      default:
        return {
          icon: Trophy,
          title: "نتيجة اللعبة",
          subtitle: "",
          color: "text-white",
          bgColor: "from-studio-light to-studio",
          iconColor: "text-white"
        };
    }
  };

  if (isLoading) {
    return (
      <div className="studio-background min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="loading-spinner w-16 h-16 mx-auto mb-4"></div>
          <p className="text-white font-body text-xl">جاري تحميل النتائج...</p>
        </div>
      </div>
    );
  }

  const config = getStatusConfig();
  const StatusIcon = config.icon || Trophy;

  return (
    <div className="studio-background min-h-screen p-4 md:p-8">
      <div className="max-w-3xl mx-auto">
        {/* Result Header */}
        <div className={`text-center py-12 rounded-2xl bg-gradient-to-br ${config.bgColor} border border-white/10 mb-8 animate-fade-in-up`}>
          <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-white/10 flex items-center justify-center">
            <StatusIcon className={`w-12 h-12 ${config.iconColor}`} />
          </div>
          
          <h1 className={`font-heading text-4xl md:text-5xl ${config.color} mb-2`}>
            {config.title}
          </h1>
          
          <p className="font-body text-white/70 text-lg mb-6">
            {config.subtitle}
          </p>
          
          <div className="bg-black/20 rounded-xl p-6 mx-4 md:mx-12">
            <p className="text-white/60 font-body text-sm mb-2">جائزتك النهائية</p>
            <p className={`font-numbers text-5xl md:text-6xl font-bold ${config.color}`}>
              {results?.final_prize?.toLocaleString() || 0}
            </p>
            <p className="text-white/60 font-body text-sm mt-2">ريال</p>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-4 mb-8">
          <div className="bg-white/5 rounded-xl p-4 text-center border border-white/10 animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
            <p className="text-green-400 font-numbers text-3xl font-bold">
              {results?.total_correct || 0}
            </p>
            <p className="text-white/60 font-body text-sm mt-1">إجابات صحيحة</p>
          </div>
          
          <div className="bg-white/5 rounded-xl p-4 text-center border border-white/10 animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
            <p className="text-white font-numbers text-3xl font-bold">
              {results?.total_questions || 0}
            </p>
            <p className="text-white/60 font-body text-sm mt-1">أسئلة مجابة</p>
          </div>
        </div>

        {/* Questions Review */}
        <div className="mb-8">
          <h2 className="font-heading text-2xl text-white mb-4">مراجعة الأسئلة</h2>
          
          <div className="space-y-3">
            {results?.questions?.map((q, idx) => (
              <div
                key={idx}
                className="bg-white/5 rounded-xl border border-white/10 overflow-hidden animate-fade-in-up"
                style={{ animationDelay: `${0.3 + idx * 0.05}s` }}
              >
                <button
                  data-testid={`question-review-${idx + 1}`}
                  onClick={() => setExpandedQuestion(expandedQuestion === idx ? null : idx)}
                  className="w-full p-4 flex items-center justify-between hover:bg-white/5 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <span className={`
                      w-8 h-8 rounded-full flex items-center justify-center
                      ${q.is_correct ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}
                    `}>
                      {q.is_correct ? <CheckCircle className="w-5 h-5" /> : <XCircle className="w-5 h-5" />}
                    </span>
                    <span className="text-white/80 font-body text-sm md:text-base text-right">
                      السؤال {q.number}: {q.question?.question_text?.substring(0, 50)}...
                    </span>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <span className="text-neon-gold font-numbers text-sm">
                      {q.prize?.toLocaleString()}
                    </span>
                    {expandedQuestion === idx ? 
                      <ChevronUp className="w-5 h-5 text-white/50" /> : 
                      <ChevronDown className="w-5 h-5 text-white/50" />
                    }
                  </div>
                </button>
                
                {expandedQuestion === idx && (
                  <div className="px-4 pb-4 border-t border-white/10 pt-4">
                    <p className="text-white font-body mb-4">{q.question?.question_text}</p>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {['A', 'B', 'C', 'D'].map(opt => {
                        const optText = q.question?.[`option_${opt.toLowerCase()}`];
                        const isCorrect = q.question?.correct_option === opt;
                        const isUserAnswer = q.user_answer === opt;
                        
                        return (
                          <div
                            key={opt}
                            className={`
                              p-3 rounded-lg flex items-center gap-2
                              ${isCorrect ? 'bg-green-500/20 border border-green-500/50' : ''}
                              ${isUserAnswer && !isCorrect ? 'bg-red-500/20 border border-red-500/50' : ''}
                              ${!isCorrect && !isUserAnswer ? 'bg-white/5' : ''}
                            `}
                          >
                            <span className="font-numbers text-neon-gold">{opt}:</span>
                            <span className="text-white/80 font-body text-sm">{optText}</span>
                            {isCorrect && <CheckCircle className="w-4 h-4 text-green-400 mr-auto" />}
                            {isUserAnswer && !isCorrect && <XCircle className="w-4 h-4 text-red-400 mr-auto" />}
                          </div>
                        );
                      })}
                    </div>
                    
                    {q.question?.explanation && (
                      <div className="mt-4 p-3 bg-neon-cyan/10 rounded-lg border border-neon-cyan/30">
                        <p className="text-neon-cyan font-body text-sm">
                          <strong>التوضيح:</strong> {q.question.explanation}
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col md:flex-row gap-4">
          <Button
            data-testid="play-again-btn"
            onClick={() => navigate("/category")}
            className="flex-1 py-4 font-heading text-lg bg-gradient-to-r from-neon-gold to-neon-gold-dim text-studio rounded-full hover:from-neon-gold-dim hover:to-neon-gold"
          >
            <RotateCcw className="ml-2 w-5 h-5" />
            العب مرة أخرى
          </Button>
          
          <Button
            data-testid="home-btn"
            onClick={() => navigate("/")}
            variant="outline"
            className="flex-1 py-4 font-body text-lg border-white/30 text-white hover:bg-white/10 rounded-full"
          >
            <Home className="ml-2 w-5 h-5" />
            الصفحة الرئيسية
          </Button>
        </div>
      </div>
    </div>
  );
}
